import React, { createContext, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import { useStore } from 'zustand';
import { createFilterStore } from './FilterStore';

// 스토어를 전달하기 위한 React Context 생성
const FilterStoreContext = createContext({});

/**
 * FilterStoreProvider 컴포넌트
 * 이 컴포넌트는 자식 컴포넌트들에게 독립적인 필터 스토어를 제공합니다.
 * @param {{ children: React.ReactNode, initialFilters: object }} props
 */
export const FilterStoreProvider = ({ children, initialFilters }) => {
  const storeRef = useRef();

  if (!storeRef.current) {
    storeRef.current = createFilterStore(initialFilters);
  }

  return (
    <FilterStoreContext.Provider value={storeRef.current}>
      {children}
    </FilterStoreContext.Provider>
  );
};

FilterStoreProvider.propTypes = {
  children: PropTypes.node.isRequired,
  initialFilters: PropTypes.object.isRequired,
};

/**
 * useFilterStore 커스텀 훅
 * Provider를 통해 제공된 필터 스토어의 상태와 액션에 접근할 수 있게 합니다.
 * 반드시 FilterStoreProvider 내부에서 사용해야 합니다.
 * @param {function} selector - 스토어에서 특정 상태를 선택하는 함수
 * @returns 선택된 상태
 */
export const useFilterStore = (selector) => {
  const store = useContext(FilterStoreContext);
  if (!store) {
    throw new Error('useFilterStore must be used within a FilterStoreProvider');
  }
  // useStore 훅을 사용하여 스토어의 상태 변화를 구독하고, selector로 선택된 부분만 반환합니다.
  return useStore(store, selector);
};
